import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * /device — a signed-in person approves (or denies) a device that asked to
 * sign them in to HQ, typically a Gantry box: the device shows a code like
 * BCDF-GHJK, the person types it here, checks what is asking, and decides.
 * API: GET/POST /device-approvals (packages/api/src/services/system/device-authorizations/).
 *
 * A ViewStore: it holds the form and the ONE pending request being looked at,
 * which is not an entity of any ServiceStore (device-approvals has no list).
 */

/** What the API shows for a pending code. */
export interface DeviceApprovalView {
  userCode: string;
  clientName: string;
  deviceId: string;
  requestedAt: string;
  expiresAt: string;
  /** The approver uses two-factor: approving needs their current authenticator code. */
  twoFactorRequired?: boolean;
}

export type DeviceApprovalState = "idle" | "looking-up" | "found" | "deciding" | "approved" | "denied";

interface RawService {
  find: (params: { query: { userCode: string } }) => Promise<DeviceApprovalView>;
  create: (data: { userCode: string; decision: "approve" | "deny"; twoFactorCode?: string }) => Promise<DeviceApprovalView & { status: string }>;
}

/** The API's sentence when it gave one, else ours. */
const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

export default class DeviceApprovalStore {
  rootStore: RootStore;
  code = "";
  state: DeviceApprovalState = "idle";
  request: DeviceApprovalView | null = null;
  error: string | null = null;
  /** The approver's authenticator code, asked for only when twoFactorRequired. */
  twoFactorCode = "";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get raw(): RawService | null {
    const app = (this.rootStore as unknown as { app?: { service: (p: string) => RawService } }).app;
    return app?.service("device-approvals") ?? null;
  }

  /** Route entry: a code from `?code=` is looked up at once. */
  initialize(code?: unknown): void {
    this.reset();
    if (typeof code === "string" && code.trim()) {
      this.code = code.trim().toUpperCase();
      void this.lookUp();
    }
  }

  reset(): void {
    this.code = "";
    this.state = "idle";
    this.request = null;
    this.error = null;
    this.twoFactorCode = "";
  }

  setTwoFactorCode(value: string): void {
    this.twoFactorCode = value.replace(/\D/g, "").slice(0, 8);
  }

  /** Approve is offered once a two-factor approver has typed a full code. */
  get canApprove(): boolean {
    return this.state === "found" && (!this.request?.twoFactorRequired || this.twoFactorCode.length >= 6);
  }

  setCode(value: string): void {
    this.code = value.toUpperCase();
    if (this.state !== "looking-up" && this.state !== "deciding") {
      this.state = "idle";
      this.request = null;
      this.error = null;
    }
  }

  get canLookUp(): boolean {
    return this.code.replace(/[^A-Za-z]/g, "").length === 8 && this.state !== "looking-up";
  }

  async lookUp(): Promise<void> {
    const raw = this.raw;
    if (!raw || !this.canLookUp) return;
    this.state = "looking-up";
    this.error = null;
    this.request = null;
    try {
      const request = await raw.find({ query: { userCode: this.code } });
      runInAction(() => {
        this.request = request;
        this.state = "found";
      });
    } catch (err) {
      // A sign-out: reset() already put the page back to idle.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.state = "idle";
        this.error = messageOf(err, "That code could not be checked. Try again.");
      });
    }
  }

  async decide(decision: "approve" | "deny"): Promise<void> {
    const raw = this.raw;
    const request = this.request;
    if (!raw || !request || this.state !== "found") return;
    this.state = "deciding";
    this.error = null;
    try {
      // Denying never needs the code; approving sends it only when asked for.
      const twoFactorCode = decision === "approve" && request.twoFactorRequired ? this.twoFactorCode : undefined;
      await raw.create({ userCode: request.userCode, decision, ...(twoFactorCode ? { twoFactorCode } : {}) });
      runInAction(() => {
        this.state = decision === "approve" ? "approved" : "denied";
      });
    } catch (err) {
      // A sign-out: reset() put the page back to idle with no request, and
      // "found" over a null request is a state no screen can render.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.state = "found";
        this.error = messageOf(err, "That decision could not be saved. Try again.");
      });
    }
  }
}
